import { api } from '../console/api.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, icon, lineName, ROLE, when } from '../console/format.ts';
import { moduleLabel } from '../console/modules.ts';
import { NOTICE_TONE, type NoticeTone } from '../console/notice.ts';
import { type Actions, current, render } from '../console/router.ts';
import { administersNubi } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import {
  numberControl,
  REQUIRED_HINT,
  selectControl,
  switchControl,
  textControl,
} from '../controls.ts';

/**
 * «Administración de Nubi › Agencias» (ADR-0023, ADR-0024): the platform's agencies as data —
 * name, code, NIT, the carriers each works with (and its agent code with each), status and the
 * invitation of its direction. The administration of Nubi registers an agency («Nueva agencia»:
 * its organization in Stytch and the invitation by e-mail), edits its data, suspends or
 * reactivates it (confirmed on this screen, never with a browser dialog) and reads the
 * «Bitácora»: who changed what and when, before → after. It never sees an agency's policies,
 * customers, payments or commissions.
 *
 * Since #273 (ADR-0025) each agency has its «Aseguradoras» panel: the carriers it works with, its
 * agent and provisional codes, grace days, the hours agreed with each carrier's desk, where its
 * commission statement comes from, its commission rate per plan (in force, and scheduled from a
 * later date — never retroactive), and its credentials with the carrier: only whether they are
 * set and when they changed, with a write-only field to replace them.
 */

export type PlatformAgency = {
  id: string;
  name: string;
  legalName: string;
  nit: string | null;
  status: 'ACTIVE' | 'SUSPENDED' | string;
  registeredAt: string;
  contact: { name: string; email: string; phone: string | null };
  invitation: { status: 'PENDING' | 'SENT' | 'CONFIGURED' | string; at: string | null };
  carriers: { id: string; name: string; agentCode: string | null }[];
  /** Only in the answer to an edit that changed the contact e-mail (#279): its invitation. */
  contactInvitation?: { email: string; outcome: 'INVITED' | 'ALREADY_MEMBER' | 'PENDING' | string };
};
/** An agency's appointment with a carrier as «Aseguradoras» shows it (never its credentials). */
export type CarrierTerms = {
  enabled: boolean;
  agentCode: string | null;
  provisionalProducerCode: string | null;
  graceDays: number;
  deskSlaHours: Record<string, number>;
  statementSource: 'INTEGRATION' | 'BY_HAND' | string;
};
export type PlatformAgencyCarrier = {
  carrier: string;
  name: string;
  configured: boolean;
  terms: CarrierTerms | null;
  credentials: { set: boolean; changedAt: string | null };
  rates: {
    plan: string;
    current: { rate: string; effectiveFrom: string } | null;
    scheduled: { rate: string; effectiveFrom: string }[];
  }[];
};
export type AgencyCarriers = { agencyId: string; today: string; carriers: PlatformAgencyCarrier[] };
export type AuditEntry = {
  id: number;
  at: string;
  action: string;
  agencyId: string;
  actor: { memberId: string; email: string | null };
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};

const STATUS: Record<string, [state: string, label: string]> = {
  ACTIVE: ['ok', 'Activa'],
  SUSPENDED: ['bad', 'Suspendida'],
};
const INVITATION: Record<string, [state: string, label: string]> = {
  SENT: ['ok', 'Invitación enviada'],
  PENDING: ['warn', 'Invitación pendiente'],
  CONFIGURED: ['info', 'Configurada en Stytch'],
};
export const ACTION: Record<string, string> = {
  SEED: 'Registró la agencia de demostración',
  CREATE: 'Dio de alta la agencia',
  UPDATE: 'Editó los datos',
  SUSPEND: 'Suspendió la agencia',
  REACTIVATE: 'Reactivó la agencia',
  INVITE: 'Invitó a la dirección',
  CARRIER_UPDATE: 'Cambió el acuerdo con la aseguradora',
  RATES_SCHEDULE: 'Programó tasas de comisión',
  CREDENTIALS_REPLACE: 'Reemplazó las credenciales',
  // The people of Nubi's own organization (#274).
  ADMIN_INVITE: 'Invitó a una persona de Nubi',
  ADMIN_ROLE_CHANGE: 'Cambió el rol de una persona de Nubi',
  ADMIN_REMOVE: 'Quitó el acceso a una persona de Nubi',
  // The platform's carriers (#283).
  PLATFORM_CARRIER_UPDATE: 'Cambió una aseguradora de la plataforma',
  CARRIER_PILOT: 'Cambió la agencia piloto',
  // The platform's notice to every agency (#284).
  NOTICE_PUBLISH: 'Publicó el aviso a las agencias',
  NOTICE_UPDATE: 'Cambió el aviso a las agencias',
  NOTICE_CLEAR: 'Quitó el aviso a las agencias',
  // Each agency's module switches (#285).
  MODULE_DISABLE: 'Apagó un módulo de la agencia',
  MODULE_ENABLE: 'Encendió un módulo de la agencia',
};
/** The hours agreed with a carrier's desk, per kind of work order, as «Órdenes» names them. */
const DESK_KIND: Record<string, string> = {
  QUOTE: 'Cotización',
  ISSUE: 'Emisión',
  ENDORSE: 'Endoso',
  CANCEL: 'Cancelación',
  RENEW: 'Renovación',
  CHANGE: 'Modificación',
  UNDERWRITING: 'Suscripción',
  GROUP_ISSUE: 'Emisión colectiva',
  GROUP_INCLUSION: 'Inclusión en colectiva',
  GROUP_EXCLUSION: 'Exclusión de colectiva',
  GROUP_CANCELLATION: 'Cancelación de certificados',
  REGULATOR_FILING: 'Registro ante la SIB',
  APPOINTMENT: 'Código de agente',
};
const SOURCE: Record<string, string> = {
  INTEGRATION: 'Llega por la integración',
  BY_HAND: 'Se digita en Nubi',
};
const CREDENTIALS: Record<string, string> = {
  SET: 'Puestas',
  NOT_SET: 'No puestas',
  REPLACED: 'Reemplazadas',
};
/** «AUTO:ATR» → «Autos · ATR». */
const planName = (plan: string) => {
  const [line = '', option = ''] = plan.split(':');
  return `${lineName(line)} · ${option.replace(/_/g, ' ')}`;
};
const percent = (r: string) => `${r.replace('.', ',')} %`;
/** The fields of an entry, as the screen names them (anything else is not shown). */
const FIELD: Record<string, string> = {
  name: 'Nombre comercial',
  legalName: 'Razón social',
  nit: 'NIT',
  'contact.name': 'Directora o director',
  'contact.email': 'Correo de contacto',
  'contact.phone': 'Teléfono de contacto',
  status: 'Estado',
  email: 'Correo invitado',
  carrier: 'Aseguradora',
  enabled: 'Trabaja con ella',
  agentCode: 'Código de agente',
  provisionalProducerCode: 'Código provisional',
  graceDays: 'Días de gracia',
  statementSource: 'Estado de cuenta',
  effectiveFrom: 'Rige desde',
  'rates.effectiveFrom': 'Tasas desde',
  'rates.plans': 'Planes con tasa',
  credentials: 'Credenciales',
  // A carrier of the platform (#283): its status and public data, and the pilot mark.
  platformStatus: 'Estado en Nubi',
  contactName: 'Contacto',
  contactEmail: 'Correo del contacto',
  contactPhone: 'Teléfono del contacto',
  environmentUrl: 'URL de su ambiente',
  pilot: 'Agencia piloto',
  // The platform's notice (#284): what it says, its tone and its last day.
  text: 'Aviso',
  tone: 'Tono',
  endsOn: 'Vigente hasta',
};
/** A carrier's status in the platform (#283). */
const PLATFORM_STATUS: Record<string, string> = {
  ACTIVE: 'Activa',
  TESTING: 'En pruebas',
  OFF: 'Apagada',
};
/** The fields of an entry about a person of Nubi (#274): who it is about and the role. */
const ADMIN_FIELD: Record<string, string> = { name: 'Persona', email: 'Correo', role: 'Rol' };
/** The name of a field of an entry, or null when the screen does not show it. */
const fieldName = (k: string) =>
  FIELD[k] ??
  (k.startsWith('deskSlaHours.')
    ? `Horas de ${(DESK_KIND[k.slice(13)] ?? k.slice(13)).toLowerCase()}`
    : /^rates\.[A-Z_]+:[A-Z0-9_]+$/.test(k)
      ? `Tasa de ${planName(k.slice(6))}`
      : null);
/** The carriers an entry may name (their code in the log). */
const CARRIER: Record<string, string> = { gyt: 'Seguros G&T' };

/** What the screen shows besides the lists: a form, a confirmation, or an agency's carriers. */
type Panel =
  | { kind: 'new' }
  | { kind: 'edit'; id: string }
  | { kind: 'confirm'; id: string; action: 'suspend' | 'reactivate' }
  | { kind: 'carriers'; id: string; carrier: string | null };
let panel: Panel | null = null;
/** The agency the «Bitácora» shows ('' = every agency). */
let auditAgency = '';
/** The agencies of the last paint (the edit form and the confirmation read them). */
let shown: PlatformAgency[] = [];
/** The carriers of the agency whose «Aseguradoras» panel is open (its forms read them). */
let carriersShown: AgencyCarriers | null = null;

/** Back to the plain list (a new session, or the menu). */
export function resetAgencias() {
  panel = null;
  auditAgency = '';
  shown = [];
  carriersShown = null;
}

/** «Seguros G&T · NUBI-001»: the carrier and the agency's agent code with it, when known. */
const carrierCell = (c: PlatformAgency['carriers'][number]) =>
  `<span class="nowrap">${esc(c.name)}${c.agentCode ? ` <span class="muted">·</span> <code>${esc(c.agentCode)}</code>` : ''}</span>`;

/** The log's agency value for the platform itself (#274): Nubi's own organization. */
export const PLATFORM_AGENCY = 'nubi';
const nameOf = (id: string) =>
  id === PLATFORM_AGENCY ? 'Nubi (plataforma)' : (shown.find((a) => a.id === id)?.name ?? id);

/** One value of an entry, in words. */
function valueText(key: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const t = String(v);
  if (key === 'status') return STATUS[t]?.[1] ?? t;
  if (key === 'carrier') return CARRIER[t] ?? t;
  if (key === 'enabled' || key === 'pilot') return v === true ? 'Sí' : 'No';
  if (key === 'platformStatus') return PLATFORM_STATUS[t] ?? t;
  if (key === 'tone') return NOTICE_TONE[t as NoticeTone]?.label ?? t;
  if (key === 'endsOn') return date(t);
  if (key === 'statementSource') return SOURCE[t] ?? t;
  if (key === 'credentials') return CREDENTIALS[t] ?? t;
  if (key === 'role') return ROLE[t] ?? t;
  if (key === 'graceDays') return `${t} días`;
  if (key === 'effectiveFrom' || key === 'rates.effectiveFrom') return date(t);
  if (key.startsWith('deskSlaHours.')) return `${t} h`;
  if (/^rates\.[A-Z_]+:/.test(key)) return percent(t);
  return t;
}

/** `{contact: {phone: x}}` → `[['contact.phone', x]]`. */
const flat = (o: Record<string, unknown> | null, prefix = ''): [string, unknown][] =>
  Object.entries(o ?? {}).flatMap(([k, v]) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? flat(v as Record<string, unknown>, `${prefix}${k}.`)
      : [[`${prefix}${k}`, v] as [string, unknown]],
  );

/** «Teléfono de contacto: — → 5512-3344»; without a before, only what was set. */
export function changeText(e: AuditEntry) {
  // A module switched (#285): «Prospectos: Encendido → Apagado».
  if (e.action === 'MODULE_DISABLE' || e.action === 'MODULE_ENABLE') {
    const on = (x: Record<string, unknown> | null) =>
      x?.enabled === false ? 'Apagado' : 'Encendido';
    return `${moduleLabel(String(e.after?.module ?? e.before?.module ?? ''))}: ${on(e.before)} → ${on(e.after)}`;
  }
  // A removal (#274) has only a before: it says whom it was about.
  if (!e.after && e.before) return changeText({ ...e, before: null, after: e.before });
  const admin = e.action.startsWith('ADMIN_');
  const name = admin ? (k: string) => ADMIN_FIELD[k] ?? null : fieldName;
  const before = new Map(flat(e.before));
  const parts = flat(e.after)
    .filter(([k]) => name(k))
    .filter(([k, v]) => e.before || (v !== null && v !== '' && k !== 'status'))
    .map(([k, v]) =>
      // The carrier (or the person) is who the entry is about, not a change: no «antes».
      e.before && before.has(k) && k !== 'carrier' && !(admin && before.get(k) === v)
        ? `${name(k)}: ${valueText(k, before.get(k))} → ${valueText(k, v)}`
        : `${name(k)}: ${valueText(k, v)}`,
    );
  return parts.join('; ') || '—';
}

export const who = (e: AuditEntry) =>
  e.actor.email ?? (e.actor.memberId.startsWith('system:') ? 'Nubi (sistema)' : e.actor.memberId);

/**
 * The toast of an edit: when the contact e-mail changed (#279) it also says what became of the
 * invitation of that person as the agency's direction.
 */
export function savedText(name: string, invitation: PlatformAgency['contactInvitation']) {
  const saved = `Datos de ${name} guardados.`;
  if (!invitation) return saved;
  if (invitation.outcome === 'PENDING')
    return `${saved} La invitación a ${invitation.email} quedó pendiente: reinténtala.`;
  if (invitation.outcome === 'ALREADY_MEMBER')
    return `${saved} ${invitation.email} ya era parte de la agencia.`;
  return `${saved} Invitamos a ${invitation.email} como dirección.`;
}

/** «Nueva agencia» or «Editar»: its data and its direction's (invited when it is registered). */
function formHtml(a: PlatformAgency | null) {
  const v = (x: string | null | undefined) => x ?? '';
  return `<form id="agency-form" class="card" data-component="agency-form" novalidate>
    <div class="card-head"><h2>${a ? `Editar ${esc(a.name)}` : 'Nueva agencia'}</h2></div>
    ${REQUIRED_HINT}
    <div class="form-grid">
      <fieldset><legend>La agencia</legend>
        ${field('Nombre comercial', textControl({ id: 'agency-name', value: v(a?.name), maxlength: 120, autocomplete: 'organization' }), { required: true })}
        ${a ? '' : field('Código', textControl({ id: 'agency-id', maxlength: 40, placeholder: 'ixchel', hint: 'Minúsculas, números o guiones; no cambia después.', autocapitalize: 'off', spellcheck: 'false' }), { required: true })}
        ${field('Razón social', textControl({ id: 'agency-legal', value: v(a?.legalName), maxlength: 160 }), { required: true })}
        ${field('NIT', textControl({ id: 'agency-nit', value: v(a?.nit), maxlength: 15, placeholder: '4478120-6' }), { required: true })}
      </fieldset>
      <fieldset><legend>Su dirección</legend>
        ${field('Nombre de la directora o director', textControl({ id: 'agency-contact-name', value: v(a?.contact.name), maxlength: 120, autocomplete: 'name' }), { required: true })}
        ${field('Correo de la directora o director', textControl({ id: 'agency-contact-email', type: 'email', value: v(a?.contact.email), maxlength: 254, autocomplete: 'email', hint: a ? 'Si cambias el correo, invitamos a esa persona como dirección.' : 'Le enviamos la invitación para entrar.' }), { required: true })}
        ${field('Teléfono de contacto', textControl({ id: 'agency-contact-phone', type: 'tel', value: v(a?.contact.phone), maxlength: 20, autocomplete: 'tel' }))}
      </fieldset>
    </div>
    <div class="form-foot"><span class="muted small">${a ? 'El código de la agencia no cambia.' : 'Nubi crea su organización en Stytch e invita a su dirección por correo.'}</span>
      <span class="spacer"></span>
      <button type="button" class="btn" data-agency-cancel>Cancelar</button>
      <button type="submit" class="btn primary" id="agency-submit">${a ? 'Guardar cambios' : 'Dar de alta e invitar'}</button></div>
  </form>`;
}

/** Suspending or reactivating says its consequence and waits for a second, deliberate click. */
function confirmHtml(a: PlatformAgency, action: 'suspend' | 'reactivate') {
  const suspend = action === 'suspend';
  return `<section class="card" role="alertdialog" aria-modal="false" aria-labelledby="agency-confirm-title" aria-describedby="agency-confirm-text" data-component="agency-confirm">
    <div class="card-head"><h2 id="agency-confirm-title">${suspend ? `¿Suspender ${esc(a.name)}?` : `¿Reactivar ${esc(a.name)}?`}</h2></div>
    <p class="note ${suspend ? 'bad' : 'info'}" id="agency-confirm-text">${icon(suspend ? 'alert' : 'ok')}<span>${
      suspend
        ? 'Sus personas no podrán entrar a Nubi ni operar desde su siguiente acción. Sus datos se conservan y puedes reactivarla cuando quieras.'
        : 'Sus personas vuelven a entrar a Nubi con todo como lo dejaron.'
    }</span></p>
    <div class="form-foot"><span class="spacer"></span>
      <button type="button" class="btn" data-agency-cancel>Cancelar</button>
      <button type="button" class="btn ${suspend ? 'danger' : 'primary'}" data-agency-confirm="${esc(a.id)}" data-agency-action="${action}">${suspend ? 'Sí, suspender' : 'Sí, reactivar'}</button></div>
  </section>`;
}

/** The agreement with the carrier: whether it works with it, its codes, grace days, statement, SLA. */
function termsHtml(c: PlatformAgencyCarrier) {
  const t: CarrierTerms = c.terms ?? {
    enabled: false,
    agentCode: null,
    provisionalProducerCode: null,
    graceDays: 30,
    deskSlaHours: {},
    statementSource: 'INTEGRATION',
  };
  const kinds = [
    ...new Set([
      ...Object.keys(DESK_KIND).filter((k) => !['REGULATOR_FILING', 'APPOINTMENT'].includes(k)),
      ...Object.keys(t.deskSlaHours),
    ]),
  ];
  const v = (x: string | null) => x ?? '';
  return `<form id="carrier-terms-form" class="carrier-part" data-carrier="${esc(c.carrier)}" novalidate aria-labelledby="carrier-terms-title">
    <h3 id="carrier-terms-title">Acuerdo con ${esc(c.name)}</h3>
    ${REQUIRED_HINT}
    <div class="form-grid">
      <fieldset><legend>La agencia y ${esc(c.name)}</legend>
        <div class="field">${switchControl({ id: 'carrier-enabled', checked: t.enabled }, 'Trabaja con esta aseguradora')}</div>
        ${field('Código de agente', textControl({ id: 'carrier-agent-code', value: v(t.agentCode), maxlength: 40, placeholder: 'NUBI-001', autocapitalize: 'characters', spellcheck: 'false', hint: 'Con él la aseguradora le manda su estado de cuenta y sus avisos.' }))}
        ${field('Código provisional de productor', textControl({ id: 'carrier-provisional-code', value: v(t.provisionalProducerCode), maxlength: 40, placeholder: 'MOR-PROV', autocapitalize: 'characters', spellcheck: 'false', hint: 'Con él vende quien todavía no tiene código propio.' }))}
        ${field('Días de gracia', numberControl({ id: 'carrier-grace-days', value: String(t.graceDays), min: 0, max: 120, hint: 'Días que un recibo vencido sigue pendiente.' }), { required: true })}
        ${field(
          'Estado de cuenta de comisiones',
          selectControl(
            { id: 'carrier-statement-source', label: 'Estado de cuenta de comisiones' },
            Object.entries(SOURCE).map(([value, label]) => ({
              value,
              label,
              selected: value === t.statementSource,
            })),
          ),
        )}
      </fieldset>
      <fieldset><legend>Horas acordadas con su escritorio</legend>
        <p class="muted small">Tiempo que tiene la aseguradora para resolver cada orden de trabajo.</p>
        <div class="sla-grid">${kinds
          .map((k) =>
            field(
              DESK_KIND[k] ?? k,
              numberControl({
                id: `carrier-sla-${k.toLowerCase().replace(/_/g, '-')}`,
                'data-sla-kind': k,
                value: t.deskSlaHours[k] === undefined ? '' : String(t.deskSlaHours[k]),
                min: 1,
                max: 720,
              }),
            ),
          )
          .join('')}</div>
      </fieldset>
    </div>
    <div class="form-foot"><span class="muted small">Cada cambio queda en la bitácora.</span><span class="spacer"></span>
      <button type="submit" class="btn primary" id="carrier-terms-submit">Guardar acuerdo</button></div>
  </form>`;
}

/** Whether the credentials are set and when they changed; a write-only field replaces them. */
function credentialsHtml(c: PlatformAgencyCarrier) {
  const state = c.credentials.set
    ? `${pill('ok', 'Puestas')} <span class="muted small">Cambiadas el ${esc(c.credentials.changedAt ? date(c.credentials.changedAt) : '—')}</span>`
    : `${pill('warn', 'No puestas')} <span class="muted small">Sin ellas Nubi no puede llamar a ${esc(c.name)} en producción.</span>`;
  return `<form id="carrier-credentials-form" class="carrier-part" data-carrier="${esc(c.carrier)}" novalidate aria-labelledby="carrier-credentials-title" data-component="carrier-credentials">
    <h3 id="carrier-credentials-title">Credenciales con ${esc(c.name)}</h3>
    <p class="credentials-state" id="carrier-credentials-state">${state}</p>
    <p class="note info">${icon('lock')}<span>Nubi las guarda en un almacén de secretos: nadie las ve, ni aquí ni en la bitácora. Solo se pueden reemplazar.</span></p>
    <div class="form-grid">
      ${field('Contraseña nueva con la aseguradora', textControl({ id: 'carrier-password', type: 'password', autocomplete: 'new-password', maxlength: 200, 'password-toggle': true, spellcheck: 'false' }), { required: true })}
    </div>
    <div class="form-foot"><span class="spacer"></span>
      <button type="submit" class="btn" id="carrier-credentials-submit"${c.configured ? '' : ' disabled'}>Reemplazar credenciales</button></div>
  </form>`;
}

/** The commission rate of each plan: in force (since when) and scheduled; a form schedules one. */
function ratesHtml(c: PlatformAgencyCarrier, today: string) {
  const rows = c.rates
    .map(
      (r) => `<tr data-plan="${esc(r.plan)}"><th scope="row">${esc(planName(r.plan))}</th>
        <td class="num">${r.current ? esc(percent(r.current.rate)) : '—'}</td>
        <td class="nowrap">${r.current ? esc(date(r.current.effectiveFrom)) : '—'}</td>
        <td>${
          r.scheduled.length
            ? r.scheduled
                .map(
                  (x) =>
                    `<span class="nowrap">${pill('info', percent(x.rate))} desde ${esc(date(x.effectiveFrom))}</span>`,
                )
                .join('<br>')
            : '<span class="muted">—</span>'
        }</td></tr>`,
    )
    .join('');
  const plans = c.rates.map((r) => ({ value: r.plan, label: planName(r.plan) }));
  return `<section class="carrier-part" aria-labelledby="carrier-rates-title" data-component="carrier-rates">
    <h3 id="carrier-rates-title">Tasas de comisión con ${esc(c.name)}</h3>
    <p class="muted small">Porcentaje de la prima neta por plan. Un cambio rige desde la fecha que digas, hoy o después: lo ya emitido conserva su tasa.</p>
    <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Tasas de comisión por plan, vigentes y programadas</caption>
      <thead><tr><th scope="col">Plan</th><th scope="col" class="num">Tasa vigente</th><th scope="col">Rige desde</th><th scope="col">Programadas</th></tr></thead>
      <tbody>${rows}</tbody></table></div>
    <form id="carrier-rate-form" data-carrier="${esc(c.carrier)}" novalidate aria-labelledby="carrier-rate-title">
      <h4 id="carrier-rate-title">Programar un cambio de tasa</h4>
      <div class="form-grid rate-grid">
        ${field('Plan', selectControl({ id: 'rate-plan', label: 'Plan' }, plans), { required: true })}
        ${field('Tasa nueva (%)', textControl({ id: 'rate-value', inputmode: 'decimal', maxlength: 6, placeholder: '15', autocomplete: 'off' }), { required: true })}
        ${field('Rige desde', textControl({ id: 'rate-from', type: 'date', min: today, value: today, hint: 'Hoy o después; nunca hacia atrás.' }), { required: true })}
      </div>
      <div class="form-foot"><span class="spacer"></span>
        <button type="submit" class="btn primary" id="carrier-rate-submit"${c.configured ? '' : ' disabled'}>Programar tasa</button></div>
    </form>
  </section>`;
}

/** «Aseguradoras» of an agency: one carrier at a time (its agreement, credentials and rates). */
function carriersHtml(a: PlatformAgency, data: AgencyCarriers, chosen: string | null) {
  const c = data.carriers.find((x) => x.carrier === chosen) ?? data.carriers[0];
  const tabs =
    data.carriers.length > 1
      ? `<div class="toolbar" role="group" aria-label="Aseguradora">${data.carriers
          .map(
            (x) =>
              `<button type="button" class="btn sm" data-carrier-tab="${esc(x.carrier)}" aria-pressed="${x === c}">${esc(x.name)}</button>`,
          )
          .join('')}</div>`
      : '';
  const status = c
    ? c.terms?.enabled
      ? pill('ok', 'Trabaja con ella')
      : pill('warn', c.configured ? 'No trabaja con ella' : 'Sin configurar')
    : '';
  return `<section class="card" data-component="agency-carriers" aria-labelledby="agency-carriers-title">
    <div class="card-head"><h2 id="agency-carriers-title">Aseguradoras de ${esc(a.name)}</h2><span class="spacer"></span>
      <button type="button" class="btn sm" data-agency-cancel>Cerrar</button></div>
    ${tabs}
    ${
      c
        ? `<div class="carrier-head"><strong>${esc(c.name)}</strong> ${status}</div>
      ${termsHtml(c)}${ratesHtml(c, data.today)}${credentialsHtml(c)}`
        : '<p class="muted small">Nubi todavía no tiene integración con ninguna aseguradora.</p>'
    }
  </section>`;
}

function auditHtml(entries: AuditEntry[], agencies: PlatformAgency[]) {
  const filter = selectControl({ id: 'audit-agency', label: 'Agencia' }, [
    { value: '', label: 'Todas las agencias', selected: auditAgency === '' },
    ...agencies.map((a) => ({ value: a.id, label: a.name, selected: a.id === auditAgency })),
  ]);
  const rows = entries
    .map(
      (e) => `<tr><td class="nowrap">${esc(when(e.at))}</td><td>${esc(who(e))}</td>
        <td>${esc(nameOf(e.agencyId))}</td><td>${esc(ACTION[e.action] ?? e.action)}</td>
        <td>${esc(changeText(e))}</td></tr>`,
    )
    .join('');
  return `<section class="card" data-component="platform-audit"><div class="card-head"><h2>Bitácora</h2><span class="spacer"></span><span class="muted small">Quién cambió qué y cuándo</span> <button type="button" class="btn sm" data-go="bitacora">Ver toda la bitácora</button></div>
    <div class="toolbar"><div class="field">${filter}</div></div>
    ${
      entries.length
        ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Bitácora de administración de las agencias, lo más reciente primero</caption>
      <thead><tr><th scope="col">Cuándo</th><th scope="col">Quién</th><th scope="col">Agencia</th><th scope="col">Qué hizo</th><th scope="col">Antes → después</th></tr></thead>
      <tbody>${rows}</tbody></table></div>`
        : `<p class="muted small" style="padding:0 var(--s-5) var(--s-5)">Todavía no hay cambios${auditAgency ? ' de esta agencia' : ''}.</p>`
    }</section>`;
}

export async function agencias() {
  const newButton = `<button type="button" class="btn primary" data-agency-new>${icon('building')}Nueva agencia</button>`;
  const description =
    'Las agencias que trabajan con Nubi: su código, su NIT, las aseguradoras con las que trabajan, su estado y la invitación de su dirección.';
  if (!administersNubi())
    return `${pageHead('Agencias', description)}${empty('lock', 'Esta sección es de la administración de Nubi', 'Tu rol no administra las agencias de la plataforma.', '', 2)}`;
  const head = pageHead('Agencias', description, panel?.kind === 'new' ? '' : newButton);
  const p = panel;
  const [agencies, audit, carriers] = await Promise.all([
    api<PlatformAgency[]>('GET', '/v1/platform/agencies'),
    api<AuditEntry[]>(
      'GET',
      auditAgency
        ? `/v1/platform/audit?agency=${encodeURIComponent(auditAgency)}`
        : '/v1/platform/audit',
    ),
    p?.kind === 'carriers'
      ? api<AgencyCarriers>('GET', `/v1/platform/agencies/${encodeURIComponent(p.id)}/carriers`)
      : Promise.resolve(null),
  ]);
  shown = agencies;
  carriersShown = carriers;
  const ofCarriers = p?.kind === 'carriers' ? agencies.find((a) => a.id === p.id) : undefined;
  const editing = p?.kind === 'edit' ? agencies.find((a) => a.id === p.id) : null;
  const confirming = p?.kind === 'confirm' ? agencies.find((a) => a.id === p.id) : undefined;
  const top =
    panel?.kind === 'new'
      ? formHtml(null)
      : editing
        ? formHtml(editing)
        : confirming && p?.kind === 'confirm'
          ? confirmHtml(confirming, p.action)
          : ofCarriers && carriers && p?.kind === 'carriers'
            ? carriersHtml(ofCarriers, carriers, p.carrier)
            : '';
  if (agencies.length === 0)
    return `${head}${top}${empty('building', 'Todavía no hay agencias', 'Da de alta la primera con «Nueva agencia»: Nubi invita a su dirección por correo.', '', 2)}`;
  const rows = agencies
    .map((a) => {
      const [state, label] = STATUS[a.status] ?? ['warn', a.status];
      const [inv, invLabel] = INVITATION[a.invitation.status] ?? ['warn', a.invitation.status];
      const buttons = [
        `<button type="button" class="btn sm" data-agency-edit="${esc(a.id)}" aria-label="Editar ${esc(a.name)}">Editar</button>`,
        `<button type="button" class="btn sm" data-agency-carriers="${esc(a.id)}" aria-label="Aseguradoras de ${esc(a.name)}">Aseguradoras</button>`,
        a.invitation.status === 'PENDING'
          ? `<button type="button" class="btn sm" data-agency-invite="${esc(a.id)}">Reintentar invitación</button>`
          : '',
        a.status === 'SUSPENDED'
          ? `<button type="button" class="btn sm" data-agency-reactivate="${esc(a.id)}" aria-label="Reactivar ${esc(a.name)}">Reactivar</button>`
          : `<button type="button" class="btn sm danger" data-agency-suspend="${esc(a.id)}" aria-label="Suspender ${esc(a.name)}">Suspender</button>`,
      ].filter(Boolean);
      return `<tr data-agency="${esc(a.id)}"><td><strong>${esc(a.name)}</strong></td><td><code>${esc(a.id)}</code></td>
        <td class="nowrap">${a.nit ? esc(a.nit) : '—'}</td>
        <td>${a.carriers.length ? a.carriers.map(carrierCell).join('<br>') : '<span class="muted">Ninguna</span>'}</td>
        <td>${pill(state, label)}</td><td>${pill(inv, invLabel)}</td>
        <td class="actions"><span class="row-actions">${buttons.join(' ')}</span></td></tr>`;
    })
    .join('');
  return `${head}${top}<section class="card" data-component="platform-agencies"><div class="card-head"><h2>Agencias de la plataforma</h2><span class="spacer"></span><span class="muted small">${agencies.length} ${agencies.length === 1 ? 'agencia' : 'agencias'}</span></div>
    <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Agencias de la plataforma</caption>
    <thead><tr><th scope="col">Agencia</th><th scope="col">Código</th><th scope="col">NIT</th><th scope="col">Aseguradoras</th><th scope="col">Estado</th><th scope="col">Invitación</th><th scope="col" class="actions">Acciones</th></tr></thead>
    <tbody>${rows}</tbody></table></div></section>${auditHtml(audit, agencies)}`;
}

/** Paints the screen again and puts the focus where the person continues. */
async function repaint(focus?: string) {
  await render(current, { quiet: true });
  if (focus) document.querySelector<HTMLElement>(focus)?.focus();
}

const value = (id: string) => ($(`#${id}`) as HTMLInputElement | null)?.value?.trim() ?? '';

/** The form as the API takes it (checked here first, so the person fixes it in place). */
function formData(creating: boolean) {
  const id = creating ? textInput('#agency-id', 'el código de la agencia') : null;
  if (id !== null && !/^[a-z][a-z0-9-]{2,39}$/.test(id))
    throw new InputError(
      'El código va en minúsculas, de 3 a 40 letras, números o guiones, y empieza con una letra (por ejemplo ixchel).',
      'agency-id',
    );
  const name = textInput('#agency-name', 'el nombre comercial');
  const legalName = textInput('#agency-legal', 'la razón social');
  const nit = textInput('#agency-nit', 'el NIT').toUpperCase();
  if (!/^[0-9]{1,12}-?[0-9K]$/.test(nit))
    throw new InputError('Escribe un NIT de Guatemala, por ejemplo 4478120-6.', 'agency-nit');
  const contactName = textInput('#agency-contact-name', 'el nombre de la directora o director');
  const email = textInput('#agency-contact-email', 'el correo de la directora o director');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
    throw new InputError('Escribe un correo válido.', 'agency-contact-email');
  const phone = value('agency-contact-phone') || null;
  return {
    ...(id ? { id } : {}),
    name,
    legalName,
    nit,
    contact: { name: contactName, email: email.toLowerCase(), phone },
  };
}

/** The carrier whose forms are open, as the last paint had it. */
const shownCarrier = (form: HTMLElement) =>
  carriersShown?.carriers.find((c) => c.carrier === form.dataset.carrier) ?? null;
const carrierUrl = (c: PlatformAgencyCarrier, rest = '') =>
  `/v1/platform/agencies/${encodeURIComponent(carriersShown?.agencyId ?? '')}/carriers/${encodeURIComponent(c.carrier)}${rest}`;

/** The agreement as the API takes it (checked here first, so the person fixes it in place). */
function termsData(): CarrierTerms {
  const code = (id: string, what: string) => {
    const x = value(id).toUpperCase();
    if (x && !/^[A-Z0-9][A-Z0-9-]{0,39}$/.test(x))
      throw new InputError(`${what}: letras, números o guiones, sin espacios.`, id);
    return x || null;
  };
  const whole = (id: string, what: string, min: number, max: number) => {
    const raw = value(id);
    const n = Number(raw);
    if (!/^\d+$/.test(raw) || n < min || n > max)
      throw new InputError(`${what}: un número entero de ${min} a ${max}.`, id);
    return n;
  };
  const deskSlaHours: Record<string, number> = {};
  for (const el of document.querySelectorAll<HTMLInputElement>('[data-sla-kind]')) {
    const kind = el.dataset.slaKind ?? '';
    if (!value(el.id)) continue;
    deskSlaHours[kind] = whole(
      el.id,
      `Horas de ${(DESK_KIND[kind] ?? kind).toLowerCase()}`,
      1,
      720,
    );
  }
  return {
    enabled: Boolean(($('#carrier-enabled') as HTMLInputElement | null)?.checked),
    agentCode: code('carrier-agent-code', 'El código de agente'),
    provisionalProducerCode: code('carrier-provisional-code', 'El código provisional'),
    graceDays: whole('carrier-grace-days', 'Los días de gracia', 0, 120),
    deskSlaHours,
    statementSource: value('carrier-statement-source') || 'INTEGRATION',
  };
}

const open = (p: Panel | null, focus?: string) => {
  panel = p;
  void repaint(focus);
};

export const actions: Actions = {
  click: {
    agencyNew: () => open({ kind: 'new' }, '#agency-name'),
    agencyCarriers: (_b, d) =>
      open(
        { kind: 'carriers', id: d.agencyCarriers ?? '', carrier: null },
        '#agency-carriers-title',
      ),
    carrierTab: (_b, d) =>
      panel?.kind === 'carriers'
        ? open({ ...panel, carrier: d.carrierTab ?? null }, '#agency-carriers-title')
        : undefined,
    agencyEdit: (_b, d) => open({ kind: 'edit', id: d.agencyEdit ?? '' }, '#agency-name'),
    agencyCancel: () => open(null, '[data-agency-new]'),
    agencySuspend: (_b, d) =>
      open(
        { kind: 'confirm', id: d.agencySuspend ?? '', action: 'suspend' },
        '[data-agency-cancel]',
      ),
    agencyReactivate: (_b, d) =>
      open(
        { kind: 'confirm', id: d.agencyReactivate ?? '', action: 'reactivate' },
        '[data-agency-cancel]',
      ),
    agencyConfirm: (b, d) => {
      const id = d.agencyConfirm ?? '';
      const suspend = d.agencyAction === 'suspend';
      const name = nameOf(id);
      void act(b, suspend ? `Suspendiendo ${name}…` : `Reactivando ${name}…`, async () => {
        await api(
          'POST',
          `/v1/platform/agencies/${encodeURIComponent(id)}/${suspend ? 'suspension' : 'reactivation'}`,
        );
        panel = null;
        await repaint(`[data-agency="${CSS.escape(id)}"] button`);
        return suspend
          ? `${name} quedó suspendida: sus personas ya no entran a Nubi.`
          : `${name} quedó activa otra vez.`;
      });
    },
    agencyInvite: (b, d) => {
      const id = d.agencyInvite ?? '';
      void act(b, 'Enviando la invitación…', async () => {
        const a = await api<PlatformAgency>(
          'POST',
          `/v1/platform/agencies/${encodeURIComponent(id)}/invitation`,
        );
        await repaint();
        return `Invitación enviada a ${a.contact?.email ?? 'su dirección'}.`;
      });
    },
  },
  submit: {
    'carrier-terms-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      const c = shownCarrier(form);
      if (!c) return;
      const name = nameOf(carriersShown?.agencyId ?? '');
      void act(form.querySelector<HTMLElement>('[type="submit"]'), 'Guardando…', async () => {
        await api('PUT', carrierUrl(c), termsData());
        await repaint('#agency-carriers-title');
        return `Acuerdo de ${name} con ${c.name} guardado.`;
      });
    },
    'carrier-rate-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      const c = shownCarrier(form);
      if (!c) return;
      const today = carriersShown?.today ?? '';
      void act(form.querySelector<HTMLElement>('[type="submit"]'), 'Programando…', async () => {
        const plan = value('rate-plan');
        if (!plan) throw new InputError('Elige el plan.', 'rate-plan');
        const rate = value('rate-value').replace(',', '.');
        if (!/^\d{1,3}(\.\d{1,2})?$/.test(rate) || Number(rate) <= 0 || Number(rate) > 100)
          throw new InputError(
            'Escribe la tasa como un porcentaje mayor que 0 y hasta 100, por ejemplo 17,5.',
            'rate-value',
          );
        const from = value('rate-from');
        if (!/^\d{4}-\d{2}-\d{2}$/.test(from))
          throw new InputError('Elige desde qué día rige.', 'rate-from');
        if (from < today)
          throw new InputError(
            `Un cambio de tasa rige de hoy (${date(today)}) en adelante, nunca hacia atrás.`,
            'rate-from',
          );
        await api('POST', carrierUrl(c, '/rates'), {
          effectiveFrom: from,
          rates: { [plan]: rate },
        });
        await repaint(`#app tr[data-plan="${CSS.escape(plan)}"] th`);
        return `${planName(plan)}: ${percent(rate)} desde el ${date(from)}.`;
      });
    },
    'carrier-credentials-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      const c = shownCarrier(form);
      if (!c) return;
      const name = nameOf(carriersShown?.agencyId ?? '');
      const input = $('#carrier-password') as HTMLInputElement | null;
      void act(
        form.querySelector<HTMLElement>('[type="submit"]'),
        'Guardando en el almacén de secretos…',
        async () => {
          const password = input?.value ?? '';
          if (!password.trim())
            throw new InputError(
              'Escribe la contraseña nueva para reemplazar las credenciales.',
              'carrier-password',
            );
          // Sent once and forgotten: the field is emptied before anything else.
          if (input) input.value = '';
          await api('PUT', carrierUrl(c, '/credentials'), { credentials: { password } });
          await repaint('#carrier-credentials-state');
          return `Credenciales de ${name} con ${c.name} reemplazadas.`;
        },
      );
    },
    'agency-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      const editing = panel?.kind === 'edit' ? panel.id : null;
      void act(form.querySelector<HTMLElement>('[type="submit"]'), 'Guardando…', async () => {
        const body = formData(!editing);
        const a = await api<PlatformAgency>(
          editing ? 'PUT' : 'POST',
          editing
            ? `/v1/platform/agencies/${encodeURIComponent(editing)}`
            : '/v1/platform/agencies',
          body,
        );
        panel = null;
        await repaint();
        if (editing) return savedText(body.name, a.contactInvitation);
        return a.invitation?.status === 'PENDING'
          ? `${body.name} quedó registrada, pero Stytch no respondió: la invitación quedó pendiente. Usa «Reintentar invitación».`
          : `${body.name} quedó registrada. Invitamos a ${body.contact.email} a entrar.`;
      });
    },
  },
  change: {
    'audit-agency': (control) => {
      auditAgency = control.value;
      void repaint('#audit-agency');
    },
  },
};
